import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { ArrowLeft, FileUp, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { VoiceTextarea } from '@/components/ui/voice-textarea';
import { AIThinkingLabel } from '@/components/ai/AIThinkingLabel';
import { useFeatureRegistration } from '@/contexts/FeatureRegistryContext';
import { useBuyerRequests, type CustomerWantRow } from '@/hooks/useBuyerRequests';
import { supabase } from '@/integrations/supabase/client';
import { GRAMS_PER_LB } from '@/lib/buyRequestParser';
import { formatNumber } from '@/lib/utils';

export const FEATURE_META = {
  id: 'load-customer-wants',
  label: 'Import multiple',
  description:
    "Paste, upload or say your customers' wants; Cannafax turns each into a shared Buy Request for your team, matched against supply, with customer names kept private from suppliers.",
  triggerType: 'dialog' as const,
  personas: ['buyer_seller'] as Array<'buyer' | 'buyer_seller' | 'seller'>,
};

type ReadRow = {
  customer_name: string | null; customer_company: string | null; want: string; read: boolean;
  productForm?: string | null; strainQuery?: string | null; species?: string | null; environment?: string | null;
  minQuantityG?: number | null; maxPricePerG?: number | null; minThcPercent?: number | null; neededBy?: string | null;
  constraints?: unknown[];
};
type Row = ReadRow & { key: number; include: boolean };

const MEDIA = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];

async function toBase64(f: File): Promise<string> {
  const buf = new Uint8Array(await f.arrayBuffer());
  let s = '';
  for (let i = 0; i < buf.length; i += 0x8000) s += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(s);
}

async function sheetToText(f: File): Promise<string> {
  if (!/\.xlsx$/i.test(f.name)) return f.text();
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await f.arrayBuffer());
  const lines: string[] = [];
  wb.worksheets[0]?.eachRow((row) => {
    lines.push((row.values as unknown[]).slice(1).map((v) => (v && typeof v === 'object' && 'text' in (v as object) ? (v as { text: string }).text : String(v ?? ''))).join(' | '));
  });
  return lines.join('\n');
}

export function toWantRow(r: ReadRow): CustomerWantRow {
  return {
    customer_name: r.customer_name?.trim() || null,
    customer_company: r.customer_company?.trim() || null,
    raw_text: r.want,
    product_form: r.productForm ?? null,
    strain_query: r.strainQuery ?? null,
    species: r.species ?? null,
    environment: r.environment ?? null,
    min_quantity_g: r.minQuantityG ?? null,
    max_price_per_g: r.maxPricePerG ?? null,
    min_thc_percent: r.minThcPercent ?? null,
    needed_by: r.neededBy ?? null,
    parsed_query: r.constraints ? { constraints: r.constraints } : null,
  };
}

export default function LoadCustomerWantsDialog({ open, onOpenChange, onLoaded, onBack }: {
  open: boolean; onOpenChange: (o: boolean) => void; onLoaded?: () => void; onBack?: () => void;
}) {
  useFeatureRegistration(FEATURE_META);
  const { bulkCreate } = useBuyerRequests();
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [reading, setReading] = useState(false);
  const [rows, setRows] = useState<Row[] | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => { setText(''); setFile(null); setRows(null); setReading(false); };

  const read = async () => {
    setReading(true);
    try {
      let body: Record<string, unknown> = { text };
      if (file) {
        if (MEDIA.includes(file.type)) body = { text, file: { mime: file.type, data: await toBase64(file), name: file.name } };
        else body = { text: [text, await sheetToText(file)].filter(Boolean).join('\n') };
      }
      const { data, error } = await supabase.functions.invoke('parse-demand-book', { body });
      if (error) {
        const detail = error instanceof FunctionsHttpError ? await error.context.json().catch(() => null) : null;
        toast.error(detail?.error ?? "We couldn't read that list just now", { description: 'Nothing was added. Please try again.' });
        return;
      }
      const got = (data?.rows ?? []) as ReadRow[];
      setRows(got.map((r, i) => ({ ...r, key: i, include: r.read })));
    } catch {
      toast.error("We couldn't read that file", { description: 'Try a PDF, photo, .xlsx or .csv, or paste the list.' });
    } finally {
      setReading(false);
    }
  };

  const chosen = (rows ?? []).filter((r) => r.include && r.read);
  const patch = (key: number, p: Partial<Row>) => setRows((rs) => rs && rs.map((r) => (r.key === key ? { ...r, ...p } : r)));

  const add = async () => {
    try {
      const created = await bulkCreate.mutateAsync(chosen.map(toWantRow));
      const fits = created.reduce((s, c) => s + (c.matches > 0 ? 1 : 0), 0);
      toast.success(`${created.length} want${created.length === 1 ? '' : 's'} added for your team`, {
        description: fits ? `${fits} already fit${fits === 1 ? 's' : ''} supply you can reach.` : 'Your Next Move™ will tell you the moment supply fits.',
      });
      reset(); onLoaded?.(); onOpenChange(false);
    } catch (e) {
      const msg = String((e as { message?: unknown })?.message ?? '');
      if (msg.includes('buyer_request_limit')) toast.error("Your plan's Buy Request limit would be passed", { description: 'Load fewer wants, or close finished requests first.' });
      else if (msg.includes('demand_book_too_many')) toast.error('That is more wants than one load allows', { description: 'Split the list into smaller loads.' });
      else toast.error("We couldn't add those wants", { description: 'Nothing was added. Please try again.' });
    }
  };

  const lbs = (g?: number | null) => (g ? `${formatNumber(g / GRAMS_PER_LB, 0)} lbs` : null);
  const price = (g?: number | null) => (g ? `≤ $${formatNumber(Math.round(g * GRAMS_PER_LB), 0)}/lb` : null);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-2xl lg:max-w-3xl flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>{rows ? 'Review Buy Requests' : 'Import Buy Requests'}</DialogTitle>
          <DialogDescription>
            {rows
              ? 'Your whole team will see who each want is for. Suppliers see the want only — never your customer.'
              : 'Paste or upload your customers’ requests. Customer names stay private to your team.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overlay-scroll flex-1 min-h-0 -mx-1 px-1">
          {!rows ? (
            <>
              <VoiceTextarea
                autoFocus value={text} onValueChange={setText} onChange={(e) => setText(e.target.value)}
                disabled={reading} rows={6} maxLength={60000}
                placeholder={'e.g. Steve at High Plains – 50 lbs indoor Gelato under $1,300/lb\nMaria (Green Leaf) needs 20 lbs smalls by Friday'}
              />
              <div className="flex flex-wrap items-center gap-2">
                <input ref={fileRef} type="file" className="hidden" accept=".csv,.txt,.xlsx,.pdf,image/png,image/jpeg,image/webp"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                <Button type="button" variant="outline" size="sm" disabled={reading} onClick={() => fileRef.current?.click()}>
                  <FileUp className="w-4 h-4 mr-1.5" />{file ? 'Change file' : 'Add a file'}
                </Button>
                {file && <span className="text-sm text-muted-foreground truncate max-w-[16rem]">{file.name}</span>}
              </div>
              {reading && <div className="text-sm text-muted-foreground"><AIThinkingLabel context="search" title="Reading your customers’ wants…" /></div>}
            </>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border">
              {rows.map((r) => (
                <li key={r.key} className={`p-3 space-y-2 ${r.include ? '' : 'opacity-60'}`}>
                  <div className="flex items-start gap-3">
                    <Checkbox className="mt-1" checked={r.include && r.read} disabled={!r.read}
                      aria-label="Include this want" onCheckedChange={(v) => patch(r.key, { include: v === true })} />
                    <div className="min-w-0 flex-1 space-y-2">
                      <p className="text-sm font-medium">{r.want}</p>
                      {r.read ? (
                        <p className="text-xs text-muted-foreground">
                          {[r.strainQuery, r.productForm, lbs(r.minQuantityG), price(r.maxPricePerG), r.neededBy && `by ${r.neededBy}`].filter(Boolean).join(' · ') || 'Open request'}
                        </p>
                      ) : (
                        <p className="text-xs text-destructive">We couldn’t read this one — it won’t be added.</p>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Input aria-label="Customer name" placeholder="Customer name" value={r.customer_name ?? ''} onChange={(e) => patch(r.key, { customer_name: e.target.value })} />
                        <Input aria-label="Customer company" placeholder="Customer company" value={r.customer_company ?? ''} onChange={(e) => patch(r.key, { customer_company: e.target.value })} />
                      </div>
                    </div>
                    <Button variant="quiet" size="icon" aria-label="Remove want" onClick={() => setRows((rs) => rs && rs.filter((x) => x.key !== r.key))}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter className="shrink-0">
          {rows ? (
            <>
              <Button variant="quiet" onClick={() => setRows(null)}>Back</Button>
              <Button disabled={!chosen.length || bulkCreate.isPending} onClick={add}>
                Add <span className="num mx-1">{chosen.length}</span> want{chosen.length === 1 ? '' : 's'}
              </Button>
            </>
          ) : (
            <>
              {onBack && <Button variant="quiet" disabled={reading} onClick={() => { reset(); onBack(); }}>
                <ArrowLeft className="w-4 h-4" /> Single request
              </Button>}
              <Button disabled={reading || (!file && text.trim().length < 3)} onClick={read}>Read wants</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
